import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShoppingBag, Package, Sparkles, Star, Coins } from 'lucide-react';
import clsx from 'clsx';
import { shopApi, type ShopItem } from '@/api/shop';
import { apiClient } from '@/api/client';

function BalanceDisplay() {
  const { data } = useQuery({
    queryKey: ['swipa-balance'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ confirmed: number; pending: number; available: number }>('/api/relay/balance');
      return data;
    },
    refetchInterval: 15000,
  });
  if (!data) return <span className="text-surface-500 text-xs">—</span>;
  const swipa = (data.available / 100000).toFixed(5).replace(/\.?0+$/, '');
  return (
    <span className="text-yellow-400 font-bold text-sm">{swipa}</span>
  );
}

const CATEGORIES = [
  { key: '', label: 'Все', icon: ShoppingBag },
  { key: 'NICK_ICON', label: 'Иконки', icon: Star },
  { key: 'NICK_COLOR', label: 'Цвет ника', icon: Sparkles },
  { key: 'FRIDGE_SKIN', label: 'Холодильник', icon: Package },
  { key: 'STICKER_SKIN', label: 'Стикеры', icon: Package },
  { key: 'MAGNET_SKIN', label: 'Магниты', icon: Package },
];

function ItemCard({ item, onBuy, buying, owned }: { item: ShopItem; onBuy: () => void; buying: boolean; owned: boolean }) {
  return (
    <div className="bg-surface-900 border border-surface-800 rounded-xl overflow-hidden hover:border-brand-600/50 transition-colors group">
      {/* Preview */}
      <div className="h-32 bg-surface-800 flex items-center justify-center overflow-hidden">
        {item.previewUrl ? (
          <img src={item.previewUrl} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-4xl opacity-30">
            {item.type === 'NICK_ICON' ? '⭐' :
             item.type === 'NICK_COLOR' ? '🎨' :
             item.type === 'FRIDGE_SKIN' ? '🧊' :
             item.type === 'STICKER_SKIN' ? '📝' :
             item.type === 'MAGNET_SKIN' ? '🧲' :
             item.type === 'GIFT' ? '🎁' : '✨'}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="text-surface-100 text-sm font-semibold truncate">{item.name}</h3>
        {item.description && (
          <p className="text-surface-500 text-xs mt-0.5 line-clamp-2">{item.description}</p>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="flex items-center gap-1 text-yellow-400 font-bold text-sm">
            <Coins size={14} /> {item.price}
          </span>
          {owned ? (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <Package size={12} /> В инвентаре
            </span>
          ) : (
            <button
              onClick={onBuy}
              disabled={buying}
              className="btn-primary text-xs py-1 px-3 disabled:opacity-40"
            >
              {buying ? '...' : 'Купить'}
            </button>
          )}
        </div>
        {item.stock !== null && (
          <p className="text-surface-600 text-[10px] mt-1">
            Осталось: {item.stock - item.sold}
          </p>
        )}
      </div>
    </div>
  );
}

function InventorySection() {
  const qc = useQueryClient();
  const { data: inventory, isLoading } = useQuery({
    queryKey: ['shop-inventory'],
    queryFn: () => shopApi.getInventory(),
  });

  const equipMut = useMutation({
    mutationFn: (cosmeticId: string) => shopApi.equipCosmetic(cosmeticId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop-inventory'] }),
  });

  if (isLoading) {
    return <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full" /></div>;
  }

  if (!inventory?.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-16 text-surface-500">
        <Package size={32} className="mb-3 opacity-40" />
        <p className="text-sm">Инвентарь пуст</p>
        <p className="text-xs mt-1 opacity-70">Купите что-нибудь в магазине!</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 pb-6 pt-3">
      <div className="space-y-2">
        {inventory.map((c) => {
          const iconUrl = (c.data as Record<string, string>)?.url;
          const color = (c.data as Record<string, string>)?.color;
          return (
            <div
              key={c.id}
              className={clsx(
                'flex items-center gap-3 p-3 rounded-xl border transition-colors',
                c.equipped
                  ? 'bg-brand-900/20 border-brand-700/50'
                  : 'bg-surface-900 border-surface-800',
              )}
            >
              {/* Preview */}
              <div className="w-10 h-10 rounded-lg bg-surface-800 flex items-center justify-center flex-shrink-0">
                {iconUrl ? (
                  <img src={iconUrl} alt="" className="w-[13px] h-[13px]" />
                ) : color ? (
                  <div className="w-6 h-6 rounded-full" style={{ backgroundColor: color }} />
                ) : (
                  <Sparkles size={16} className="text-surface-500" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-surface-100 text-sm font-medium truncate">{c.item.name}</p>
                <p className="text-surface-500 text-xs">{c.item.type.replace('_', ' ')}</p>
              </div>

              {/* Equip toggle */}
              <button
                onClick={() => equipMut.mutate(c.id)}
                disabled={equipMut.isPending}
                className={clsx(
                  'text-xs py-1 px-3 rounded-lg font-medium transition-colors',
                  c.equipped
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-800 text-surface-400 hover:bg-surface-700',
                )}
              >
                {c.equipped ? '✓ Активно' : 'Надеть'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ShopPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'shop' | 'inventory'>('shop');
  const [category, setCategory] = useState('');
  const [buyingId, setBuyingId] = useState<string | null>(null);

  const { data: items, isLoading } = useQuery({
    queryKey: ['shop-items', category],
    queryFn: () => shopApi.listItems(category || undefined),
  });

  const { data: inventory } = useQuery({
    queryKey: ['shop-inventory'],
    queryFn: () => shopApi.getInventory(),
  });
  const ownedItemIds = new Set(inventory?.map((c) => c.itemId) ?? []);

  const [buyStatus, setBuyStatus] = useState<string | null>(null);

  const buyMut = useMutation({
    mutationFn: async (itemId: string) => {
      setBuyingId(itemId);
      setBuyStatus('Переводим монеты...');
      return shopApi.buyItem(itemId);
    },
    onSuccess: () => {
      setBuyingId(null);
      setBuyStatus(null);
      qc.invalidateQueries({ queryKey: ['shop-items'] });
      qc.invalidateQueries({ queryKey: ['shop-inventory'] });
      qc.invalidateQueries({ queryKey: ['swipa-balance'] });
      setTab('inventory');
    },
    onError: (err: unknown) => {
      setBuyingId(null);
      setBuyStatus(null);
      const msg = (err as { response?: { status?: number; data?: { message?: string } } })?.response?.data?.message
        || (err as Error).message || 'Ошибка покупки';
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setTab('inventory');
      } else {
        alert(msg);
      }
    },
  });

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface-950">
      {/* Header with SWIPA balance */}
      <div className="h-12 border-b border-surface-800 px-4 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="btn-ghost p-1.5 text-surface-400">
          <ArrowLeft size={18} />
        </button>
        <ShoppingBag size={18} className="text-brand-400" />
        <span className="font-semibold text-surface-200">Магазин</span>
        <div className="ml-auto flex items-center gap-1.5 bg-surface-800 rounded-lg px-3 py-1.5">
          <Coins size={14} className="text-yellow-400" />
          <BalanceDisplay />
        </div>
      </div>

      {/* Tabs: Shop / Inventory */}
      <div className="flex gap-2 px-4 pt-3 pb-1 flex-shrink-0">
        <button
          onClick={() => setTab('shop')}
          className={clsx('px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
            tab === 'shop' ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400')}
        >
          <ShoppingBag size={13} className="inline mr-1" /> Магазин
        </button>
        <button
          onClick={() => setTab('inventory')}
          className={clsx('px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
            tab === 'inventory' ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400')}
        >
          <Package size={13} className="inline mr-1" /> Инвентарь
        </button>
      </div>

      {/* Buy overlay */}
      {buyStatus && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-surface-900 border border-surface-800 rounded-xl p-6 flex flex-col items-center gap-3">
            <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
            <p className="text-surface-200 text-sm font-medium">{buyStatus}</p>
          </div>
        </div>
      )}

      {tab === 'inventory' ? (
        <InventorySection />
      ) : (<>

      {/* Categories */}
      <div className="flex gap-1.5 px-4 py-3 overflow-x-auto flex-shrink-0">
        {CATEGORIES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setCategory(key)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              category === key
                ? 'bg-brand-600 text-white'
                : 'bg-surface-800 text-surface-400 hover:bg-surface-700',
            )}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* Items grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full" />
          </div>
        ) : !items?.length ? (
          <div className="text-center py-16 text-surface-500 text-sm">
            Товаров пока нет
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                owned={ownedItemIds.has(item.id)}
                onBuy={() => {
                  if (confirm(`Купить "${item.name}" за ${item.price} SWIPA?`)) {
                    buyMut.mutate(item.id);
                  }
                }}
                buying={buyingId === item.id}
              />
            ))}
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
